import type { RouteDefinition } from "@solidjs/router";
import { lazy } from "solid-js";
import { config } from "~/js/network/network-handler";
import { IndexLayoutData } from "~/solidJs/pages/(IndexLayout)/data";
import { appSumoData } from "~/solidJs/pages/[AppSumoCallbackPage]/data";
import { thirdPartySignInAndUpData } from "~/solidJs/pages/[OauthCallbackPage]/data";
import { verifyEmailDataFc } from "~/solidJs/pages/[VerifyEmailPage]/data";
import { IndexApi } from "~/solidJs/pages/Index/data";
import SuccessfulPaymentData from "~/solidJs/pages/SuccessfulPayment/data";
import { getUserAndSetStore } from "~/solidJs/shared";
import { queryClient } from "~/solidJs/shared/api/queryClient";
import { UserApi } from "~/solidJs/shared/api/user";
import { ExperimentsMethods } from "~/solidJs/shared/helpers/amplitudeExperiments";
import { FeatureFlagLinkMethods } from "~/solidJs/shared/helpers/featureFlagLink";
import type { IUser } from "~/solidJs/shared/helpers/types";
import { PersonalizedData } from "~/solidJs/shared/models/PersonalizeData";
import { Spinner } from "~/solidJs/shared/ui/atoms/Spinner/Spinner";
import { SuspenseBlock } from "~/solidJs/shared/ui/atoms/SuspenseBlock/SuspenseBlock";

type Route = {
	absolute: string;
	relative: string;
	params?: string;
	getTitle?: (...args: any) => string;
};

export const routerConfig = {
	home: {
		absolute: "/home",
		relative: "/home"
	},
	workspace: {
		absolute: "/",
		relative: "/"
	},
	aiApps: {
		absolute: "/ai-apps",
		relative: "/ai-apps"
	},
	gallery: {
		absolute: "/home/gallery",
		relative: "/gallery"
	},
	signIn: {
		absolute: "/sign-in",
		relative: "/sign-in"
	},
	signUp: {
		absolute: "/sign-up",
		relative: "/sign-up"
	},
	templates: {
		absolute: "/home/templates",
		relative: "/templates"
	},
	userWorkspace: {
		absolute: "/user-workspace",
		relative: "/user-workspace",
		params: "/user-workspace/:workspace_id"
	},
	sharedWorkspace: {
		absolute: "/workspace",
		relative: "/workspace",
		params: "/workspace/:workspace_id"
	},
	templateWorkspace: {
		absolute: "/template",
		relative: "/template",
		params: "/template/:template_id"
	},
	taskWorkspace: {
		absolute: "/task",
		relative: "/task",
		params: "/task/:task_id"
	},
	homeProjects: {
		absolute: "/home/projects",
		relative: "/projects"
	},
	pricingPage: {
		absolute: "/pricing",
		relative: "/pricing"
	},
	pricingPageUnsubscribeModal: {
		absolute: "/pricing/unsubscribe",
		relative: "/pricing/unsubscribe"
	},
	accountBuyCredits: {
		absolute: "/account/buy-credits",
		relative: "/buy-credits"
	},
	accountBuyCreditsSuccess: {
		absolute: "/account/buy-credits-success",
		relative: "/buy-credits-success"
	},
	account: {
		absolute: "/account",
		relative: "/account"
	},
	accountSubscriptionAndCredits: {
		absolute: "/account/subscription-and-credits",
		relative: "/subscription-and-credits"
	},
	accountProfile: {
		absolute: "/account/profile",
		relative: "/profile"
	},
	successfulPayment: {
		absolute: "/successful-payment",
		relative: "/successful-payment"
	},
	trainPanel: {
		absolute: "/train-panel",
		relative: "/train-panel"
	},
	trainNewModel: {
		absolute: "/train-panel/train-new-model",
		relative: "/train-new-model"
	},
	setupAccount: {
		absolute: "/setup-account",
		relative: "/setup-account"
	},
	verifyEmailPage: {
		absolute: "/verify-email",
		relative: "/verify-email"
	},
	featureFlagLink: {
		absolute: "/feature-flag-link",
		relative: "/feature-flag-link"
	},
	tutorials: {
		absolute: "/tutorials",
		relative: "/tutorials",
		params: "/tutorials/:type",
		getTitle: () => "Tutorials"
	},
	adminPanel: {
		absolute: "/admin-panel",
		relative: "/admin-panel",
		getTitle: () => `Admin panel: ${config.ENV}`
	},
	adminPanelCreateCompany: {
		absolute: "/admin-panel/create-company",
		relative: "/create-company"
	},
	adminPanelCreateCompanyPayments: {
		absolute: "/admin-panel/create-company-payments",
		relative: "/create-company-payments"
	},
	adminPanelCreateUserAccess: {
		absolute: "/admin-panel/create-user-access",
		relative: "/create-user-access"
	},
	adminPanelManageUserCompany: {
		absolute: "/admin-panel/manage-user-company",
		relative: "/manage-user-company"
	},
	adminPanelVerifyUserEmail: {
		absolute: "/admin-panel/verify-user-email",
		relative: "/verify-user-email"
	},
	adminPanelCreateCheckoutLink: {
		absolute: "/admin-panel/create-checkout-link",
		relative: "/create-checkout-link"
	},
	adminPanelManageSubscription: {
		absolute: "/admin-panel/manage-subscription",
		relative: "/manage-subscription"
	},
	adminPanelAddCredits: {
		absolute: "/admin-panel/add-credits",
		relative: "/add-credits"
	},
	adminPanelCreateCreditType: {
		absolute: "/admin-panel/create-credit-type",
		relative: "/create-credit-type"
	},
	adminPanelDisableRecurrent: {
		absolute: "/admin-panel/disable-recurrent",
		relative: "/disable-recurrent"
	},
	appSumoCallbackPage: {
		absolute: "/app-sumo-callback",
		relative: "/app-sumo-callback"
	},
	apiKeysPage: {
		absolute: "/api-keys",
		relative: "/api-keys"
	},
	adminPanelNodeConstructor: {
		absolute: "/admin-panel-node-constructor",
		relative: "/admin-panel-node-constructor",
		getTitle: () => `Node Constructor: ${config.ENV}`
	}
} as const satisfies Record<string, Route>;
const unsupportedPagesRoutes = ["/home/*", "/canvas", "/train-panel/*"] as const satisfies string[];
export const accessRedirectMap: Record<string, string> = {
	[routerConfig.trainNewModel.absolute]: routerConfig.trainPanel.absolute
};

export const oldRedirectMap: Record<string, string> = {
	[routerConfig.signUp.absolute]: routerConfig.signIn.absolute
};

export const routerRedirectsConfig = {
	afterAuth: routerConfig.workspace.absolute
} as const;
Object.freeze(routerConfig);
export const ignorePreventSave = { [routerConfig.signIn.absolute]: true };

export type AuthRouterContext = {
	user: Promise<IUser>;
};

const AdminPanelRoutes: RouteDefinition = {
	path: routerConfig.adminPanel.absolute,
	component: lazy(() => import("~/solidJs/pages/AdminPanelPages/(AdminPanelLayout)")), // Define your new layout component here
	// data: AdminPanelLayoutData, // You can add data loading for the layout if needed
	children: [
		{
			path: "/", // This will render at /admin-panel/
			component: lazy(() => import("~/solidJs/pages/AdminPanelPages/AdminPanelHomePage")) // The new home page
		},
		{
			path: routerConfig.adminPanelCreateCompany.relative,
			component: lazy(
				() => import("~/solidJs/pages/AdminPanelPages/AdminPanelCreateCompanyPage")
			) // Page for creating a company
		},
		{
			path: routerConfig.adminPanelCreateCompanyPayments.relative,
			component: lazy(
				() => import("~/solidJs/pages/AdminPanelPages/AdminPanelCreateCompanyPaymentsPage")
			) // Page for creating payments
		},
		{
			path: routerConfig.adminPanelCreateUserAccess.relative,
			component: lazy(
				() => import("~/solidJs/pages/AdminPanelPages/AdminPanelCreateUserAccessPage")
			) // Page for creating user access
		},
		{
			path: routerConfig.adminPanelManageUserCompany.relative,
			component: lazy(
				() => import("~/solidJs/pages/AdminPanelPages/AdminPanelManageUserCompanyPage")
			) // Page for managing user-company relations
		},
		{
			path: routerConfig.adminPanelVerifyUserEmail.relative,
			component: lazy(
				() => import("~/solidJs/pages/AdminPanelPages/AdminPanelVerifyUserPage")
			) // Page for manually verifying user email
		},
		{
			path: routerConfig.adminPanelCreateCheckoutLink.relative,
			component: lazy(
				() => import("~/solidJs/pages/AdminPanelPages/AdminPanelCreateCheckoutLinkPage")
			) // Page for creating checkout link
		},
		{
			path: routerConfig.adminPanelManageSubscription.relative,
			component: lazy(
				() => import("~/solidJs/pages/AdminPanelPages/AdminPanelSubscriptionManagementPage")
			) // Page for creating checkout link
		},
		{
			path: routerConfig.adminPanelAddCredits.relative,
			component: lazy(
				() => import("~/solidJs/pages/AdminPanelPages/AdminPanelAddCreditsPage")
			) // Page for adding user credits
		},
		{
			path: routerConfig.adminPanelCreateCreditType.relative,
			component: lazy(
				() => import("~/solidJs/pages/AdminPanelPages/AdminPanelCreateCreditTypePage")
			) // Page for creating credit type
		},
		{
			path: routerConfig.adminPanelDisableRecurrent.relative,
			component: lazy(
				() => import("~/solidJs/pages/AdminPanelPages/AdminPanelDisableRecurrentPage")
			) // Page for disabling recurrent payments
		}
	]
};

const AccountRoutes: RouteDefinition = {
	path: [
		routerConfig.account.absolute,
		routerConfig.accountBuyCredits.absolute,
		routerConfig.accountBuyCreditsSuccess.absolute
	],
	component: lazy(() => import("~/solidJs/pages/AccountPage")),
	children: [
		{
			path: ["/"],
			component: lazy(() => import("~/solidJs/pages/SubscriptionPage"))
		},
		{
			path: routerConfig.accountSubscriptionAndCredits.relative,
			component: lazy(() => import("~/solidJs/pages/SubscriptionAndCreditsPage"))
		},
		{
			path: routerConfig.accountProfile.relative,
			component: lazy(() => import("~/solidJs/pages/AccountProfilePage"))
		}
	]
};

const needsAccountSetupRoutes: RouteDefinition[] = [
	{
		path: [
			routerConfig.workspace.absolute,
			routerConfig.userWorkspace.params,
			routerConfig.sharedWorkspace.params,
			routerConfig.templateWorkspace.params,
			routerConfig.taskWorkspace.params
		],
		component: () => lazy(() => import("~/solidJs/pages/Index"))({}),
		children: [],
		data: IndexApi.loadIndexData.bind(IndexApi) as any
	},
	{
		path: routerConfig.tutorials.params,
		component: () => lazy(() => import("~/solidJs/pages/TutorialsPage"))({})
	},
	{
		path: routerConfig.successfulPayment.absolute,
		component: lazy(() => import("~/solidJs/pages/SuccessfulPayment")),
		data: SuccessfulPaymentData as any
	},
	{
		path: [
			routerConfig.pricingPage.absolute,
			routerConfig.pricingPageUnsubscribeModal.absolute
		],
		component: lazy(() => import("~/solidJs/pages/PricingPage"))
	},
	{
		path: "/",
		component: lazy(() => import("~/solidJs/pages/(TrainPanelLayout)")),
		children: [
			AdminPanelRoutes,
			AccountRoutes,
			{
				path: routerConfig.apiKeysPage.absolute,
				component: lazy(() => import("~/solidJs/pages/ApiKeys"))
			},
			{
				path: routerConfig.adminPanelNodeConstructor.absolute,
				component: lazy(
					() => import("~/solidJs/pages/AdminPanelPages/AdminPanelNodeConstructorPage")
				)
			},
			{
				path: unsupportedPagesRoutes,
				component: lazy(() => import("~/solidJs/pages/UnsupportedBannerPage"))
			}

			// {
			// 	path: "/canvas",
			// 	component: lazy(() => import("~/solidJs/pages/MagicCanvasPage")),
			// 	data: dataMagicCanvas
			// },
			// {
			// 	path: "/train-panel/canvas",
			// 	component: () => <Navigate href={"/canvas"} />
			// },
			// {
			// 	path: routerConfig.trainPanel.absolute,
			// 	component: lazy(
			// 		() => import("~/solidJs/pages/(TrainPanelHomeLayout)/(TrainPanelHomeLayout)")
			// 	),
			// 	data: dataTrainNewModel,
			// 	children: [
			// 		{
			// 			path: "/",
			// 			component: lazy(() => import("~/solidJs/pages/TrainPanelHomePage")),
			// 			//@ts-expect-error
			// 			data: dataTrainPanelHomePage
			// 		},
			// 		{
			// 			path: routerConfig.trainNewModel.relative,
			// 			component: () => (
			// 				<PrivateHoc
			// 					accessMap={{
			// 						premium: true,
			// 						superuser: true,
			// 						enterprise: true,
			// 						plus: true
			// 					}}
			// 				>
			// 					{lazy(() => import("~/solidJs/pages/TrainPanelTrainNewModelPage"))(
			// 						{}
			// 					)}
			// 				</PrivateHoc>
			// 			)
			// 		}
			// 	]
			// }
		]
	}
];
export const routes: RouteDefinition[] = [
	{
		path: "*404",
		component: lazy(() => import("~/solidJs/pages/404"))
	},
	{
		path: "/",
		component: lazy(() => import("~/solidJs/pages/(IndexLayout)")),
		data: IndexLayoutData,
		children: [
			{
				path: routerConfig.setupAccount.absolute,
				component: () => lazy(() => import("~/solidJs/pages/SetupAccountPage"))({})
			},
			{
				path: "/",
				component: lazy(() => import("~/solidJs/pages/(NeedsStupLayout)")),
				children: needsAccountSetupRoutes,
				data: (props) => {
					(props.data as AuthRouterContext).user.then((user: IUser) => {
						if (user) {
							ExperimentsMethods.fetchAndSetVariants(user);
							PersonalizedData.getPersonalizedDataAndSetStore();
						}
					});
					return props.data;
				}
			},
			{
				component: () => <Spinner size="large" />,
				path: routerConfig.featureFlagLink.absolute,
				data: FeatureFlagLinkMethods.routerDataFunc as any
			}
		]
	},
	{
		path: routerConfig.appSumoCallbackPage.absolute,
		data: appSumoData
	},
	{
		path: routerConfig.verifyEmailPage.absolute,
		component: lazy(() => import("~/solidJs/pages/VerifyEmailPage/VerifyEmailPage"))
	},
	{
		path: "/",
		component: () => (
			<SuspenseBlock size="large">
				{lazy(() => import("~/solidJs/pages/(AuthLayout)"))({})}
			</SuspenseBlock>
		),
		data: () => getUserAndSetStore(),
		children: [
			{
				path: routerConfig.signIn.absolute,
				component: () => lazy(() => import("~/solidJs/pages/SignInPage/SignInPage"))({}),
				data: () => {
					queryClient.prefetchQuery({
						queryKey: [UserApi.key_getUserRegion],
						queryFn: () => UserApi.getUserRegion()
					});
				}
			},
			{
				path: "/reset-password",
				component: () =>
					lazy(() => import("~/solidJs/pages/ResetPasswordPage/ResetPasswordPage"))({})
			},
			{
				path: "/auth/reset-password",
				component: () =>
					lazy(
						() =>
							import(
								"~/solidJs/pages/SubmitResetPasswordPage/SubmitResetPasswordPage"
							)
					)({})
			},
			{
				path: "/auth/callback",
				component: () => <Spinner size="large" />,
				data: thirdPartySignInAndUpData
			}
		]
	},
	{
		path: "/auth/verify-email",
		component: () => (
			<div class="relative flex h-full w-full items-center justify-center">
				<Spinner size="large" />
			</div>
		),
		data: verifyEmailDataFc
	}
];
